// CourseProgress — per-course user state (SRS box per flashcard, wrong-answer
// tracking, quiz session history).
//
// Decision (Session 19G): mirrors the v1 storage layout (CLAUDE.md §11) but
// adapted to schema-first v2:
//   - srs[fcId]: Leitner box + nextDue (ISO datetime); cards never seen are
//     absent from this dict, treated as "due now".
//   - wrongAnswers[itemId]: tracks any quiz item the user got wrong at least
//     once. Keyed by mcq.id or tf.id. Resolved when correctCount >= wrongCount
//     (computed at read-time, not stored).
//   - quizHistory: append-only log of quiz sessions; capped client-side to
//     last 50 to avoid unbounded growth.
import { z } from 'zod';
import { EssayDraftSchema, EssayAIScoreSchema } from './essay.js';
import { SelfAssessmentRecordSchema, SUBJECTIVE_REVISIT_THRESHOLD, avgSelfScore } from './self-assessment.js';
import { ItemOverrideSchema } from './item-override.js';
import { ItemFeedbackSchema } from './item-feedback.js';
import { RepairRecordSchema } from './repair.js';
import { DiagnoseVerdictSchema } from './diagnose-result.js';
export const SrsEntrySchema = z.object({
    /** Leitner box: 1 = new/failed (due now), 2..4 = learning, 5 = retired (long interval). */
    box: z.number().int().min(1).max(5),
    /** ISO datetime of last review (or initial seed). */
    lastSeen: z.string().datetime(),
    /** ISO datetime when card next becomes due. */
    nextDue: z.string().datetime(),
});
export const WrongAnswerEntrySchema = z.object({
    /** Item type — keeps mcq vs tf separable in the ledger. */
    type: z.enum(['mcq', 'tf']),
    /** caseId the item belongs to (so we can filter by case in WrongAnswersView). */
    caseId: z.string().nullable(),
    wrongCount: z.number().int().nonnegative(),
    correctCount: z.number().int().nonnegative(),
    /** ISO datetime of most-recent wrong attempt. */
    lastWrong: z.string().datetime(),
    lastSeen: z.string().datetime(),
});
export const QuizSessionRecordSchema = z.object({
    id: z.string(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime(),
    /** caseId filter active for the session, or null if 'all cases' mixed. */
    caseId: z.string().nullable(),
    totalQuestions: z.number().int().nonnegative(),
    correctCount: z.number().int().nonnegative(),
});
/** User-curated bookmark — independent from the auto-tracked wrong-answer
 *  ledger. A user may bookmark a question they got RIGHT but want to revisit.
 *  Session 19AO: type enum extended to include subjective kinds.
 *  Session 19AQ · F6: complete coverage of all defined item types — added
 *  reading_passage / glossary / lecture_note / mindmap so bookmarks span every
 *  consumable artifact (per陛下 "错题本应该能 mapping 所有我们定义的题型"). */
export const BookmarkEntrySchema = z.object({
    type: z.enum([
        'mcq', 'tf', 'flashcard', 'essay', 'free_response', 'proof_drill',
        'reading_passage', 'glossary', 'lecture_note', 'mindmap',
    ]),
    caseId: z.string().nullable(),
    /** ISO datetime when bookmarked. */
    addedAt: z.string().datetime(),
    /** Optional user note (future feature; not surfaced in UI yet). */
    note: z.string().max(500).optional(),
});
/**
 * SubjectiveAttempt — Session 19AO · LLM_NATIVE §10 + 陛下 19AO.
 *
 * Tracks user attempts on subjective items (essay / free_response /
 * proof_drill). Distinct from WrongAnswerEntry which is binary (wrong vs
 * correct count) — subjective items have multi-dimensional self-scores
 * (per the unified 0-3 SelfAssessment scale).
 *
 * "Needs revisit" predicate: avg of latest record < SUBJECTIVE_REVISIT_THRESHOLD
 * (= 2.0). Distinct from objective wrong-answers; separately tracked + stat'd.
 */
export const SubjectiveAttemptEntrySchema = z.object({
    /** Item type — keeps essay / free_response / proof_drill separable in stats. */
    type: z.enum(['essay', 'free_response', 'proof_drill']),
    /** Chapter the item belongs to (for filter / per-chapter stats). */
    caseId: z.string().nullable(),
    /** Append-only history of self-assessment records (most recent last). Capped
     *  to last 10 client-side to avoid unbounded growth. */
    records: z.array(SelfAssessmentRecordSchema),
    /** ISO datetime of most-recent attempt. */
    lastAttempted: z.string().datetime(),
});
/** In-flight quiz session state — lets the user resume mid-100-question deck. */
export const ActiveQuizSessionSchema = z.object({
    /** Same id used for the eventual QuizSessionRecord on finish. */
    id: z.string(),
    startedAt: z.string().datetime(),
    /** Filter that built the question pool. null = 'all cases' mixed. */
    caseId: z.string().nullable(),
    /** Ordered MCQ ids in the session deck — pure ids to keep storage tight. */
    questionIds: z.array(z.string()).min(1),
    /** Index of the next un-finalized question (questions before this are answered). */
    currentIdx: z.number().int().nonnegative(),
    /** Currently-locked option for currentIdx (null until user picks). */
    picked: z.number().int().min(0).max(3).nullable(),
    /** Per-question record, parallel to questionIds[0..currentIdx]. */
    attempts: z.array(z.object({
        mcqId: z.string(),
        pickedIndex: z.number().int().min(0).max(3),
        correct: z.boolean(),
    })),
});
/**
 * FreeResponseDraft — 19BO+ Line 2 B3.4 · per-user free-response attempt.
 *
 * Mirrors EssayDraft semantics: a user-typed answer (text) + commit timestamp +
 * AI-grade slot (aiScore). Distinct from IngestPackage's FreeResponse.aiScore
 * (which is the AI-original item's reservation slot, untouched by this layer).
 *
 * selfScores are NOT stored here — they flow through `subjectiveAttempts`
 * (persistSubjectiveScore). Duplicating them would cause drift.
 *
 * aiScore reuses EssayAIScoreSchema (lockstep with EssayDraft.aiScore and
 * EvaluateResult.verdict — persistence is zero-transform from the evaluate
 * endpoint's verdict).
 */
export const FreeResponseDraftSchema = z.object({
    /** FreeResponse id (starts with `fr`). */
    itemId: z.string(),
    /** User-typed answer text (may be empty mid-compose). */
    text: z.string(),
    /** ISO datetime when the user committed the answer; null if uncommitted. */
    committedAt: z.string().datetime().nullable(),
    /** Hint-progress state (how many progressive hints revealed). 0..hints.length. */
    revealedHintsCount: z.number().int().nonnegative(),
    /** AI-grade slot — null until /ingest/evaluate fills it. */
    aiScore: EssayAIScoreSchema.nullable(),
    /** ISO datetime of last write. */
    updatedAt: z.string().datetime(),
});
/**
 * DiagnosisStatsSnapshot — 19BO+ Line 2 B4.3 · 诊断发生时的错题统计标量摘要。
 *
 * 与完整 DiagnoseRequest（byCase/byCategory/byCognitive/recentTrend 数组）的区别：
 * 持久化只存 3 个标量（total/unresolved/resolved）——它们足够让 UI 显示
 * 「此诊断基于 N 道错题」的新鲜度提示，也足够让多设备合并策略（last-write-wins
 * by diagnosedAt）做判断，但不把整个 byCase 数组写进 IDB（信息效率：用户每次
 * 想看现状就重新诊断，历史快照的明细无产品价值）。
 *
 * 若 B5 学习状态机需要「基于历史诊断对比」，届时再扩字段——现在不过度设计。
 */
export const DiagnosisStatsSnapshotSchema = z.object({
    total: z.number().int().nonnegative(),
    unresolved: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
});
/**
 * DiagnosisRecord — 19BO+ Line 2 B4.3 · 单次 AI 弱项诊断的持久化记录。
 *
 * verdict: 完整 DiagnoseVerdict（weaknesses + overallSummary + suggestedPractice
 *   + 服务端注入的 diagnosedAt/modelVersion）。随 syncCourse opaque payload 同步。
 * statsSnapshot: 诊断所基于的错题统计标量（见上）。给 UI 新鲜度提示 + 合并策略。
 *
 * 与 EssayAIScore（单题评分持久化）的语义差异：
 *   evaluate 是「单题评分」（静态，随题保存），diagnose 是「课程级弱项快照」
 *   （动态，随错题变化）。所以这里存单条最新（last-write-wins），不存历史数组。
 *
 * 合并策略：多设备同步时，按 verdict.diagnosedAt（服务端可信时间戳）取较新者。
 */
export const DiagnosisRecordSchema = z.object({
    verdict: DiagnoseVerdictSchema,
    statsSnapshot: DiagnosisStatsSnapshotSchema,
});
/**
 * DiagnosisPending — B5 桥5 · 后台自动诊断产出的待审记录。
 *
 * 与 DiagnosisRecord（已 accept 的当前生效诊断）的语义分界：
 *   - diagnosis (DiagnosisRecord) = 用户已 accept，作为当前权威弱项诊断。
 *   - diagnosisPending (DiagnosisPending) = 后台自动跑出，用户尚未 review，
 *     进 accept（提升为 diagnosis）/ reject（丢弃）。
 *
 * 为什么不直接写 diagnosis：保留铁律 3（用户显式触发）精神——自动诊断未经
 * 用户确认前不成为"生效诊断"，避免用户打开 App 发现诊断已被静默替换。
 * 待审队列给了用户"看一眼再决定要不要采纳"的环节。
 *
 * queuedAt 与 verdict.diagnosedAt 的区别（两者都存，各有用途）：
 *   - verdict.diagnosedAt = LLM 产出时间（服务端注入 · 可信源）
 *   - queuedAt = 写入 pending 的前端时间（用于"待审已 X 小时"UI 新鲜度
 *     + 陈旧清理策略：超阈值未处理可自动清理，避免 pending 永久堆积）
 *   两者通常相近，但语义不同——queuedAt 衡量"用户欠审多久"，diagnosedAt
 *   衡量"数据本身多新"。
 *
 * 合并策略：与 diagnosis 一致，多设备同步按 verdict.diagnosedAt
 * last-write-wins（queuedAt 随 verdict 走）。单条（不存历史数组）——与
 * DiagnosisRecord 同理，历史快照明细无产品价值。
 *
 * 不存 triggeredBy（auto_schedule/auto_threshold）：当前唯一进 pending 的
 * 来源就是"自动"，手动诊断的 review 已在 dialog 内完成直接写 diagnosis。
 * 若未来需要区分触发来源，应在 observability 层记，不污染持久化 schema。
 */
export const DiagnosisPendingSchema = DiagnosisRecordSchema.extend({
    /** 入队时间（前端写 pending 时的时间戳）。用于"待审已 X 小时"UI + 陈旧清理。 */
    queuedAt: z.string().datetime(),
});
export const CourseProgressSchema = z.object({
    /** Matches CourseDraft.id. */
    courseId: z.string(),
    srs: z.record(SrsEntrySchema),
    wrongAnswers: z.record(WrongAnswerEntrySchema),
    quizHistory: z.array(QuizSessionRecordSchema),
    /** Session 19H: user-curated bookmarks (orthogonal to wrongAnswers). */
    bookmarks: z.record(BookmarkEntrySchema),
    /** Session 19H: in-progress quiz session, or null. */
    activeQuizSession: ActiveQuizSessionSchema.nullable(),
    /** Session 19N: essay attempts keyed by essayId; AI-score reserved. */
    essayDrafts: z.record(EssayDraftSchema),
    /** Session 19AO: subjective-item self-assessment ledger keyed by item id.
     *  Distinct from `wrongAnswers` (which is binary mcq/tf only). */
    subjectiveAttempts: z.record(SubjectiveAttemptEntrySchema).optional(),
    /** 19BO+ Line 2 B3.4: per-user free-response attempts keyed by itemId.
     *  Optional + forward-filled (additive · no DB_VERSION bump — see idb.ts
     *  getProgress forward-fill). */
    freeResponseDrafts: z.record(FreeResponseDraftSchema).optional(),
    /** 19BO+ Line 2 B4.3: AI 弱项诊断的最新一次记录（课程级快照）.
     *  Optional + forward-filled (additive · no DB_VERSION bump). null = 用户
     *  从未诊断过；有值 = 最近一次诊断 verdict + 其 stats 快照标量。随 syncCourse
     *  opaque payload 同步，合并策略按 verdict.diagnosedAt last-write-wins。 */
    diagnosis: DiagnosisRecordSchema.nullable().optional(),
    /** B5 桥5: 后台自动诊断产出的待审记录（用户尚未 accept 的 verdict）.
     *  Optional + forward-filled (additive · no DB_VERSION bump). null = 无待审；
     *  有值 = 后台跑出但用户未 review 的 verdict。accept → 提升为 diagnosis +
     *  清空本字段；reject → 直接清空。详见 DiagnosisPendingSchema。 */
    diagnosisPending: DiagnosisPendingSchema.nullable().optional(),
    /** Session 19AP · LLM_NATIVE §2.3 EditableItem落地: user edits to specific
     *  item fields (e.g. mcq.explanation), keyed by `${itemId}.${field}`.
     *  Render path resolves: override?.editedText ?? aiItem[field]. */
    itemOverrides: z.record(ItemOverrideSchema).optional(),
    /** Session 19AQ · F5: per-AI-item user feedback (flag / 1-5 rating /
     *  optional note). Same key shape as itemOverrides. Future operator-only
     *  hallucination/error-check dashboard aggregates these. */
    itemFeedback: z.record(ItemFeedbackSchema).optional(),
    /** Session 19AU · Self-Healing Repair Layer: per-item AI-generated repair
     *  records keyed by `${itemKind}:${itemId}` (use repairKey() helper).
     *  IngestPackage stays pure-AI; resolved view = repairs[k]?.current ?? pkg item. */
    repairs: z.record(RepairRecordSchema).optional(),
    /** ISO datetime — last write. Useful for sync ordering when accounts arrive. */
    updatedAt: z.string().datetime(),
});
/** Empty-state factory. */
export function emptyProgress(courseId) {
    return {
        courseId,
        srs: {},
        wrongAnswers: {},
        quizHistory: [],
        bookmarks: {},
        activeQuizSession: null,
        essayDrafts: {},
        subjectiveAttempts: {},
        freeResponseDrafts: {},
        itemOverrides: {},
        itemFeedback: {},
        repairs: {},
        updatedAt: new Date().toISOString(),
    };
}
/** Wrong-answer "unresolved" predicate (matches v1 mastery semantics). */
export function isUnresolved(entry) {
    return entry.wrongCount > entry.correctCount;
}
/** Session 19AO: subjective-item "needs revisit" predicate. Avg of latest
 *  record's per-dimension scores < threshold (2.0 = below 基本达成). */
export function subjectiveNeedsRevisit(entry) {
    const latest = entry.records[entry.records.length - 1];
    if (!latest)
        return false;
    const avg = avgSelfScore(latest);
    if (Number.isNaN(avg))
        return false;
    return avg < SUBJECTIVE_REVISIT_THRESHOLD;
}
/** Session 19AO: latest-record avg score (for sorting / display). NaN-safe. */
export function subjectiveLatestAvg(entry) {
    const latest = entry.records[entry.records.length - 1];
    if (!latest)
        return NaN;
    return avgSelfScore(latest);
}
//# sourceMappingURL=course-progress.js.map